import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Users, BarChart3, Link2, Copy, Plus, Trash2, Edit3, RefreshCw,
    CheckCircle, Clock, ExternalLink, Loader2, X, Eye, TrendingUp,
    Award, Target, Building2, Globe, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
    useAffiliateProducts, useAffiliateReferrals, useAffiliateDashboard,
    useMyAffiliateLinks, useCreateProduct, useUpdateProduct, useDeleteProduct,
    useSyncProduct, useUpdateMyLinks,
} from '../hooks/useAffiliates';

const PRODUCT_COLORS = ['#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777','#475569'];

const STATUS_BADGE = {
    registered: 'bg-blue-50 text-blue-700 border-blue-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
};

/* ── Overview Tab ────────────────────────────────────────────────────────── */
function OverviewTab() {
    const { data: dashboard, isLoading } = useAffiliateDashboard();
    const navigate = useNavigate();

    if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;
    if (!dashboard) return null;

    const kpis = [
        { label: 'Total Referrals', value: dashboard.total_referrals, icon: Users, color: 'bg-stone-900' },
        { label: 'Completed', value: dashboard.completed, icon: CheckCircle, color: 'bg-emerald-600' },
        { label: 'Registered', value: dashboard.registered, icon: Clock, color: 'bg-blue-600' },
        { label: 'Conversion Rate', value: `${dashboard.conversion_rate}%`, icon: TrendingUp, color: 'bg-indigo-600' },
    ];

    return (
        <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {kpis.map(kpi => (
                    <div key={kpi.label} className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className={`w-9 h-9 ${kpi.color} rounded-lg flex items-center justify-center mb-3`}>
                            <kpi.icon className="w-4.5 h-4.5 text-white" />
                        </div>
                        <p className="text-2xl font-bold text-stone-900">{kpi.value}</p>
                        <p className="text-xs text-stone-500 mt-0.5">{kpi.label}</p>
                    </div>
                ))}
            </div>

            {/* Per-Product Breakdown */}
            {dashboard.by_product?.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>By Product</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {dashboard.by_product.map(p => (
                            <div key={p.product_id} className="bg-white rounded-xl border border-stone-200 p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    {p.logo_url ? (
                                        <img src={p.logo_url} alt={p.name} className="w-8 h-8 rounded-lg object-cover" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                                            style={{ backgroundColor: p.color || '#4f46e5' }}>
                                            {(p.name || '?').charAt(0)}
                                        </div>
                                    )}
                                    <span className="text-sm font-semibold text-stone-900">{p.name}</span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-stone-500">
                                    <span><strong className="text-stone-900">{p.total}</strong> referrals</span>
                                    <span><strong className="text-emerald-600">{p.completed}</strong> completed</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Top Affiliates (admin) */}
            {dashboard.top_affiliates?.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Top Affiliates</h3>
                    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 text-stone-500 text-xs">
                                <tr>
                                    <th className="text-left px-4 py-2.5 font-medium">Name</th>
                                    <th className="text-right px-4 py-2.5 font-medium">Referrals</th>
                                    <th className="text-right px-4 py-2.5 font-medium">Completed</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {dashboard.top_affiliates.map(a => (
                                    <tr key={a.user_id} className="hover:bg-stone-50">
                                        <td className="px-4 py-3 font-medium text-stone-900">{a.name}</td>
                                        <td className="px-4 py-3 text-right text-stone-600">{a.total}</td>
                                        <td className="px-4 py-3 text-right text-emerald-600 font-medium">{a.completed}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Recent Referrals */}
            {dashboard.recent_referrals?.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-stone-700 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Referrals</h3>
                    <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
                        {dashboard.recent_referrals.slice(0, 8).map(r => (
                            <div key={r.referral_id} className="flex items-center justify-between px-4 py-3 hover:bg-stone-50">
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium text-stone-900">{r.name || r.email}</span>
                                    {r.company && <span className="text-xs text-stone-400 ml-2">{r.company}</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_BADGE[r.status] || STATUS_BADGE.pending}`}>
                                        {r.status}
                                    </span>
                                    {r.contact_id && (
                                        <button onClick={() => navigate(`/contacts/${r.contact_id}`)}
                                            className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700">
                                            <Eye className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── Referrals Tab ───────────────────────────────────────────────────────── */
function ReferralsTab() {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const { data: products } = useAffiliateProducts();
    const { data: referralsData, isLoading } = useAffiliateReferrals({
        status: statusFilter || undefined,
        product_id: productFilter || undefined,
    });
    const referrals = referralsData?.data || [];

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    className="px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg bg-white focus:outline-none">
                    <option value="">All statuses</option>
                    <option value="registered">Registered</option>
                    <option value="completed">Completed</option>
                </select>
                <select value={productFilter} onChange={e => setProductFilter(e.target.value)}
                    className="px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg bg-white focus:outline-none">
                    <option value="">All products</option>
                    {(products || []).map(p => (
                        <option key={p.product_id} value={p.product_id}>{p.name}</option>
                    ))}
                </select>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
            ) : referrals.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-stone-400">
                    <Users className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm font-medium text-stone-500">No referrals yet</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm min-w-[700px]">
                        <thead className="bg-stone-50 text-stone-500 text-xs">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-medium">Name</th>
                                <th className="text-left px-4 py-2.5 font-medium">Company</th>
                                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                                <th className="text-center px-3 py-2.5 font-medium">S</th>
                                <th className="text-center px-3 py-2.5 font-medium">P</th>
                                <th className="text-center px-3 py-2.5 font-medium">T</th>
                                <th className="text-center px-3 py-2.5 font-medium">E</th>
                                <th className="text-center px-3 py-2.5 font-medium">Total</th>
                                <th className="text-left px-4 py-2.5 font-medium">Referred By</th>
                                <th className="text-right px-4 py-2.5 font-medium w-16"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {referrals.map(r => (
                                <tr key={r.referral_id} className="hover:bg-stone-50">
                                    <td className="px-4 py-3">
                                        <div>
                                            <span className="font-medium text-stone-900">{r.name || '—'}</span>
                                            <span className="block text-[11px] text-stone-400">{r.email}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-stone-600">{r.company || '—'}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_BADGE[r.status] || STATUS_BADGE.pending}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 text-center text-xs font-medium">{r.assessment_data?.strategy ?? '—'}</td>
                                    <td className="px-3 py-3 text-center text-xs font-medium">{r.assessment_data?.people ?? '—'}</td>
                                    <td className="px-3 py-3 text-center text-xs font-medium">{r.assessment_data?.technology ?? '—'}</td>
                                    <td className="px-3 py-3 text-center text-xs font-medium">{r.assessment_data?.execution ?? '—'}</td>
                                    <td className="px-3 py-3 text-center text-xs font-bold text-indigo-600">{r.total_score ?? '—'}</td>
                                    <td className="px-4 py-3 text-xs text-stone-500">{r.affiliate_user_name || r.affiliate_code || '—'}</td>
                                    <td className="px-4 py-3 text-right">
                                        {r.contact_id && (
                                            <button onClick={() => navigate(`/contacts/${r.contact_id}`)}
                                                className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700" title="View Contact">
                                                <ChevronRight className="w-4 h-4" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ── My Links Tab ────────────────────────────────────────────────────────── */
function MyLinksTab() {
    const { data: links, isLoading } = useMyAffiliateLinks();
    const updateLinks = useUpdateMyLinks();
    const [editCodes, setEditCodes] = useState({});
    const [editing, setEditing] = useState(false);

    const handleSave = () => {
        updateLinks.mutate(editCodes, {
            onSuccess: () => { toast.success('Affiliate codes updated'); setEditing(false); },
            onError: () => toast.error('Failed to update'),
        });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>My Affiliate Links</h3>
                {!editing ? (
                    <Button variant="outline" size="sm" onClick={() => {
                        const codes = {};
                        (links || []).forEach(l => { codes[l.product_slug] = l.code || ''; });
                        setEditCodes(codes);
                        setEditing(true);
                    }}><Edit3 className="w-3.5 h-3.5 mr-1" /> Edit Codes</Button>
                ) : (
                    <div className="flex gap-2">
                        <Button size="sm" onClick={handleSave} disabled={updateLinks.isPending}>Save</Button>
                        <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                    </div>
                )}
            </div>

            {(links || []).length === 0 ? (
                <p className="text-sm text-stone-500">No affiliate products configured. Ask your admin to set up products.</p>
            ) : (
                <div className="space-y-3">
                    {(links || []).map(link => (
                        <div key={link.product_id} className="bg-white rounded-xl border border-stone-200 p-4">
                            <div className="flex items-center gap-3 mb-3">
                                {link.logo_url ? (
                                    <img src={link.logo_url} alt={link.product_name} className="w-8 h-8 rounded-lg object-cover" />
                                ) : (
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                                        style={{ backgroundColor: link.color || '#4f46e5' }}>
                                        {(link.product_name || '?').charAt(0)}
                                    </div>
                                )}
                                <span className="text-sm font-semibold text-stone-900">{link.product_name}</span>
                            </div>

                            {editing ? (
                                <div>
                                    <Label className="text-xs">Referral Code</Label>
                                    <Input
                                        value={editCodes[link.product_slug] || ''}
                                        onChange={e => setEditCodes(prev => ({ ...prev, [link.product_slug]: e.target.value }))}
                                        placeholder="Enter your referral code"
                                        className="mt-1"
                                    />
                                </div>
                            ) : link.code ? (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-stone-500">Code:</span>
                                        <code className="text-xs font-mono bg-stone-100 px-2 py-0.5 rounded">{link.code}</code>
                                    </div>
                                    {link.referral_url && (
                                        <div className="flex items-center gap-2">
                                            <input
                                                readOnly
                                                value={link.referral_url}
                                                className="flex-1 text-xs bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-600"
                                            />
                                            <button onClick={() => copyToClipboard(link.referral_url)}
                                                className="p-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                                                <Copy className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-xs text-stone-400">No referral code set. Click "Edit Codes" to add yours.</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── Products Tab (admin) ────────────────────────────────────────────────── */
function ProductsTab() {
    const { data: products, isLoading } = useAffiliateProducts();
    const createProduct = useCreateProduct();
    const updateProduct = useUpdateProduct();
    const deleteProduct = useDeleteProduct();
    const syncProduct = useSyncProduct();
    const [showDialog, setShowDialog] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ name: '', slug: '', api_base_url: '', api_key: '', webhook_secret: '', logo_url: '', color: '#4f46e5', referral_url_template: '' });
    const [syncing, setSyncing] = useState(null);

    const openAdd = () => {
        setEditId(null);
        setForm({ name: '', slug: '', api_base_url: '', api_key: '', webhook_secret: '', logo_url: '', color: '#4f46e5', referral_url_template: '' });
        setShowDialog(true);
    };

    const openEdit = (p) => {
        setEditId(p.product_id);
        setForm({
            name: p.name, slug: p.slug, api_base_url: p.api_base_url || '', api_key: p.api_key || '',
            webhook_secret: p.webhook_secret || '', logo_url: p.logo_url || '', color: p.color || '#4f46e5',
            referral_url_template: p.referral_url_template || '',
        });
        setShowDialog(true);
    };

    const handleSave = () => {
        if (!form.name.trim() || !form.slug.trim()) { toast.error('Name and slug are required'); return; }
        if (editId) {
            updateProduct.mutate({ productId: editId, data: form }, {
                onSuccess: () => { setShowDialog(false); toast.success('Product updated'); },
                onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to update'),
            });
        } else {
            createProduct.mutate(form, {
                onSuccess: () => { setShowDialog(false); toast.success('Product created'); },
                onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to create'),
            });
        }
    };

    const handleSync = (productId) => {
        setSyncing(productId);
        syncProduct.mutate(productId, {
            onSuccess: (data) => { toast.success(`Synced ${data.synced_users} users`); setSyncing(null); },
            onError: (e) => { toast.error(e?.response?.data?.detail || 'Sync failed'); setSyncing(null); },
        });
    };

    if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Affiliate Products</h3>
                    <p className="text-xs text-stone-400 mt-0.5">Configure external products for affiliate tracking.</p>
                </div>
                <Button size="sm" onClick={openAdd}><Plus className="w-4 h-4 mr-1" /> Add Product</Button>
            </div>

            {(products || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-stone-400 border border-dashed border-stone-300 rounded-xl">
                    <Target className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm font-medium text-stone-500">No products configured</p>
                    <p className="text-xs mt-1">Add your first affiliate product integration.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {products.map(p => (
                        <div key={p.product_id} className="bg-white rounded-xl border border-stone-200 p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    {p.logo_url ? (
                                        <img src={p.logo_url} alt={p.name} className="w-10 h-10 rounded-lg object-cover" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                                            style={{ backgroundColor: p.color || '#4f46e5' }}>
                                            {(p.name || '?').charAt(0)}
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-sm font-semibold text-stone-900">{p.name}</span>
                                        <span className="block text-[10px] text-stone-400 font-mono">{p.slug}</span>
                                    </div>
                                </div>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${p.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-stone-50 text-stone-500 border-stone-200'}`}>
                                    {p.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-stone-500 mb-3">
                                <span><strong className="text-stone-900">{p.referral_count}</strong> referrals</span>
                                <span><strong className="text-emerald-600">{p.completed_count}</strong> completed</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button onClick={() => openEdit(p)} className="px-2 py-1 text-[11px] text-stone-600 bg-stone-100 rounded-md hover:bg-stone-200">
                                    <Edit3 className="w-3 h-3 inline mr-1" />Edit
                                </button>
                                <button onClick={() => handleSync(p.product_id)} disabled={syncing === p.product_id}
                                    className="px-2 py-1 text-[11px] text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 disabled:opacity-50">
                                    {syncing === p.product_id ? <Loader2 className="w-3 h-3 inline mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 inline mr-1" />}
                                    Sync
                                </button>
                                <button onClick={() => { if (window.confirm('Delete this product?')) deleteProduct.mutate(p.product_id); }}
                                    className="px-2 py-1 text-[11px] text-red-600 bg-red-50 rounded-md hover:bg-red-100">
                                    <Trash2 className="w-3 h-3 inline mr-1" />Delete
                                </button>
                            </div>
                            {/* Webhook URL info */}
                            <div className="mt-3 pt-3 border-t border-stone-100">
                                <p className="text-[10px] text-stone-400">Webhook URL:</p>
                                <code className="text-[10px] text-stone-500 font-mono break-all">https://api.nurturecrm.ai/api/webhooks/{p.slug}</code>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editId ? 'Edit Product' : 'Add Affiliate Product'}</DialogTitle>
                        <DialogDescription>Configure an external product for affiliate tracking.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="AI Agility Check" /></div>
                            <div><Label>Slug *</Label><Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="aiagilitycheck" /></div>
                        </div>
                        <div><Label>API Base URL</Label><Input value={form.api_base_url} onChange={e => setForm(f => ({ ...f, api_base_url: e.target.value }))} placeholder="https://aiagilitycheck.com/api/crm" /></div>
                        <div><Label>API Key</Label><Input type="password" value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} placeholder="Your API key" /></div>
                        <div><Label>Webhook Secret</Label><Input type="password" value={form.webhook_secret} onChange={e => setForm(f => ({ ...f, webhook_secret: e.target.value }))} placeholder="HMAC secret for signature verification" /></div>
                        <div><Label>Referral URL Template</Label><Input value={form.referral_url_template} onChange={e => setForm(f => ({ ...f, referral_url_template: e.target.value }))} placeholder="https://aiagilitycheck.com/?ref={code}" />
                            <p className="text-[10px] text-stone-400 mt-0.5">Use {'{code}'} as placeholder for the affiliate code.</p>
                        </div>
                        <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="https://..." /></div>
                        <div>
                            <Label>Color</Label>
                            <div className="flex items-center gap-2 mt-1">
                                {PRODUCT_COLORS.map(c => (
                                    <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                                        className={`w-7 h-7 rounded-lg transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-stone-900 scale-110' : 'hover:scale-105'}`}
                                        style={{ backgroundColor: c }} />
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={createProduct.isPending || updateProduct.isPending}>
                            {editId ? 'Update' : 'Add Product'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function Affiliates() {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('overview');
    const [refreshing, setRefreshing] = useState(false);
    const isAdmin = user?.is_admin || user?.org_role === 'owner' || user?.org_role === 'admin';

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['affiliateDashboard'] }),
            queryClient.invalidateQueries({ queryKey: ['affiliateReferrals'] }),
            queryClient.invalidateQueries({ queryKey: ['affiliateProducts'] }),
            queryClient.invalidateQueries({ queryKey: ['myAffiliateLinks'] }),
        ]);
        toast.success('Data refreshed');
        setRefreshing(false);
    };

    const tabs = [
        { key: 'overview', label: 'Overview', icon: BarChart3 },
        { key: 'referrals', label: 'Referrals', icon: Users },
        { key: 'my-links', label: 'My Links', icon: Link2 },
        ...(isAdmin ? [{ key: 'products', label: 'Products', icon: Target }] : []),
    ];

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Affiliates</h1>
                    <p className="text-stone-500 text-sm mt-1">Track referrals, affiliate performance, and product integrations.</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Tabs */}
            <div className="border-b border-stone-200">
                <div className="flex gap-0 overflow-x-auto">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === tab.key ? 'border-stone-700 text-stone-700' : 'border-transparent text-stone-500 hover:text-stone-700'
                                }`}>
                                <Icon className="w-4 h-4" />{tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {activeTab === 'overview' && <OverviewTab />}
            {activeTab === 'referrals' && <ReferralsTab />}
            {activeTab === 'my-links' && <MyLinksTab />}
            {activeTab === 'products' && isAdmin && <ProductsTab />}
        </div>
    );
}
